<template>
  <Card title="Jouw afspraak" class="mt-6">
    <p class="section-description">
      Dit product komt uit de catalogus. De prijzen zijn {{ basisLabel }} van de leverancier en
      worden bij een nieuwe prijslijst bijgewerkt. Wat je hier invult blijft van jou.
    </p>
    <p v-if="catalog?.link_status === 'partial'" class="catalog-agreement-card__detached">
      <Label variant="warning" size="sm">Deels losgekoppeld</Label>
      {{ detachedText }}
    </p>

    <div class="catalog-agreement-card__fields">
      <CatalogAgreementFields
        v-model:purchase-margin-pct="purchaseMarginPct"
        v-model:sell-type="sellType"
        v-model:sell-value="sellValue"
        :price-basis="priceBasis"
      />
    </div>

    <p v-if="error" class="catalog-agreement-card__error">{{ error }}</p>

    <Btn variant="secondary" size="sm" :loading="saving" :disabled="!dirty" @click="save">
      Afspraak opslaan
    </Btn>
  </Card>
</template>

<script>
  import { computed, ref, watch } from 'vue';
  import Card from '@/components/partials/Card.vue';
  import Btn from '@/components/partials/Btn.vue';
  import Label from '@/components/partials/Label.vue';
  import CatalogAgreementFields, {
    PRICE_BASIS_LABELS,
  } from '@/components/products/CatalogAgreementFields.vue';
  import { catalogService } from '@/services/catalogService';
  import { useToastStore } from '@/store/toastStore';

  export default {
    name: 'CatalogAgreementCard',
    components: { Card, Btn, Label, CatalogAgreementFields },
    props: {
      installationId: { type: String, required: true },
      catalog: { type: Object, default: () => ({}) },
    },
    emits: ['saved'],
    setup(props, { emit }) {
      const toastStore = useToastStore();
      const priceBasis = computed(() => props.catalog?.price_basis || 'brochure_excl_vat');
      const basisLabel = computed(() =>
        (PRICE_BASIS_LABELS[priceBasis.value] || 'brochureprijzen').toLowerCase()
      );

      const detachedText = computed(() =>
        props.catalog?.matrix_detached
          ? 'Je gebruikt een eigen matrix; zie de tab Regels.'
          : 'Je hebt meerprijzen overschreven; zie de tab Regels.'
      );

      const purchaseMarginPct = ref(0);
      const sellType = ref('percent');
      const sellValue = ref(0);
      const saving = ref(false);
      const error = ref('');

      const reset = () => {
        purchaseMarginPct.value = Number(props.catalog?.purchase_margin_pct) || 0;
        const adjustment = props.catalog?.sell_adjustment;
        const defaultType = priceBasis.value === 'net_purchase_excl_vat' ? 'margin' : 'percent';
        sellType.value = adjustment?.type || defaultType;
        sellValue.value = Number(adjustment?.value) || 0;
      };
      watch(() => props.catalog, reset, { immediate: true, deep: true });

      const dirty = computed(() => {
        const adjustment = props.catalog?.sell_adjustment;
        return (
          Number(purchaseMarginPct.value) !== (Number(props.catalog?.purchase_margin_pct) || 0) ||
          sellType.value !== (adjustment?.type || sellType.value) ||
          Number(sellValue.value) !== (Number(adjustment?.value) || 0)
        );
      });

      const save = async () => {
        saving.value = true;
        error.value = '';
        const body =
          priceBasis.value === 'net_purchase_excl_vat'
            ? {
                purchase_margin_pct: 0,
                sell_adjustment: { type: 'margin', value: Number(sellValue.value) || 0 },
              }
            : {
                purchase_margin_pct: Number(purchaseMarginPct.value) || 0,
                sell_adjustment: { type: sellType.value, value: Number(sellValue.value) || 0 },
              };
        try {
          const result = await catalogService.updateInstallation(props.installationId, body);
          toastStore.addToast({ type: 'success', message: 'Afspraak opgeslagen' });
          emit('saved', result.installation);
        } catch (err) {
          error.value = err?.response?.data?.error || 'Opslaan mislukt';
        } finally {
          saving.value = false;
        }
      };

      return {
        priceBasis,
        basisLabel,
        detachedText,
        purchaseMarginPct,
        sellType,
        sellValue,
        saving,
        error,
        dirty,
        save,
      };
    },
  };
</script>

<style scoped lang="scss">
  .catalog-agreement-card {
    &__fields {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 0.75rem;
      margin-bottom: 1rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__detached {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
      margin: 0 0 1rem;
      color: var(--slate-600);
      @include fontSize('sm');
    }

    &__error {
      margin: 0 0 0.75rem;
      color: var(--red-700);
      @include fontSize('sm');
    }
  }
</style>
